"use client";

import React, { type ReactNode, useRef, useState } from "react";

import { useUserUnengaged } from "../../hooks/useUserUnengage";
import { cn } from "../../lib/utils";
import { HoverCard, HoverCardContent, type HoverCardProps, HoverCardTrigger } from "../ui/hover-card";

interface HoverCardClickableProps extends HoverCardProps {
  triggerJSX: ReactNode | null;
  hoverDelay?: number;
  hoverExitDelay?: number;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  triggerClassName?: string;
  showArrow?: boolean;
  children: React.ReactNode;
  sideOffset?: number;
  forceOpen?: boolean;
  openOnClick?: boolean;
}

export const HoverCardClickable = React.forwardRef<HTMLDivElement, HoverCardClickableProps>(
  (
    {
      triggerJSX,
      side = "bottom",
      variant = "default",
      size = "default",
      className,
      triggerClassName,
      showArrow = false,
      openOnClick = true,
      forceOpen,
      sideOffset,
      hoverDelay = 450,
      hoverExitDelay = 800,
      children,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [enableUnengage, setEnableUnengage] = useState(false);
    const popoverMenu = useRef<HTMLDivElement>(null);
    const popoverTrigger = useRef<HTMLElement>(null);
    const onHoverTimeout = useRef<NodeJS.Timeout | null>(null);
    const triggerElement = React.isValidElement(triggerJSX) ? triggerJSX : <span>{triggerJSX}</span>;
    // STABLE identity is load-bearing: this ref feeds Radix HoverCardTrigger's composed ref
    // chain down to PopperAnchor, whose no-deps effect re-runs onAnchorChange after every
    // render. An inline callback here changes identity per render, forcing React to detach and
    // reattach the whole composed chain each render — under synchronous store-write bursts that
    // churn escalated into "Maximum update depth exceeded" (PopperAnchor) inside form sections.
    const setPopoverTriggerRef = React.useCallback((node: unknown) => {
      popoverTrigger.current = node as HTMLElement | null;
    }, []);

    const handleClose = () => {
      setOpen(false);
      setEnableUnengage(false);
    };

    // Stable array identity: useUserUnengaged lists this as an effect dependency, so a fresh
    // array literal here re-ran its listener setup/teardown on every render.
    const unengageRefs = React.useMemo(() => [popoverMenu, popoverTrigger], []);
    useUserUnengaged(unengageRefs, handleClose, hoverExitDelay, enableUnengage);

    const handleTriggerClick = () => {
      if (open) {
        handleClose();
      } else {
        if (openOnClick) handleOpen();
      }
    };

    const handleOpen = () => {
      setOpen(true);
      setTimeout(() => {
        setEnableUnengage(true);
      }, 350);
    };

    const openOnHover = () => {
      // Open after a delay set in hoverDelay
      onHoverTimeout.current = setTimeout(() => {
        handleOpen();
      }, hoverDelay);
    };

    const handleMouseLeave = () => {
      //if already open leave it up to the handleUSerUnengaged to close it
      if (open) return;
      // Cancel the open immediately if the mouse leaves
      if (onHoverTimeout.current) {
        clearTimeout(onHoverTimeout.current);
        onHoverTimeout.current = null;
      }
      handleClose();
    };

    return (
      <HoverCard open={forceOpen !== undefined ? forceOpen : open}>
        <HoverCardTrigger
          asChild
          ref={setPopoverTriggerRef}
          className={cn("cursor-pointer", triggerClassName)}
          onMouseDown={handleTriggerClick}
          onKeyDown={(e: React.KeyboardEvent) => {
            //keyboard parity with the mousedown toggle for focusable triggers (e.g. a button)
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleTriggerClick();
            }
          }}
          onMouseEnter={openOnHover}
          onMouseLeave={handleMouseLeave}
        >
          {triggerElement}
        </HoverCardTrigger>
        <HoverCardContent
          style={{ width: "min-content !important" }}
          variant={variant}
          size={size}
          side={side}
          sideOffset={sideOffset}
          ref={popoverMenu}
          className={cn("w-fit p-0", className)}
          {...props}
        >
          {children}
        </HoverCardContent>
      </HoverCard>
    );
  }
);
HoverCardClickable.displayName = "HoverCardClickable";
